import { __awaiter } from "tslib";
import React, { useCallback, useEffect, useRef, useState, } from 'react';
import { useAppDispatch } from '@/store/hooks';
import { setSessionAreaName, setSessionCollectionName, useSessionAreaName, useSessionCollectionName, } from '@/platform-services/session';
import { useAreasSelector, useCollectionsSelector, useGetConfigPageType, useUserSettings, } from '@/store/selectors';
import { addError, addSuccess } from '@/store/slices/notificationsSlice';
import { useUpdateUserSettingsMutation } from '@/store/api';
import { useGoToNewArea, useIsHomePage } from '@/routing/hooks';
import { switchSite } from '@/store/slices/sessionSlice';
import { SearchForRetail } from '@/constants';
import { ContextSelectors } from './context-selectors.component';
import { findArea, hasArea, useSetDefaultCollectionAndArea } from './utils';
import { logError } from '@/utils/logger';
import { useActiveUser } from '@/platform-services/auth';
import { useFindAreasForCollection } from '@/platform-services/areas';
export const ContextSelectorsContainer = () => {
    const goToNewArea = useGoToNewArea();
    const isHomePage = useIsHomePage();
    const dispatch = useAppDispatch();
    const [updateUserSettings] = useUpdateUserSettingsMutation();
    const activeCollectionName = useSessionCollectionName();
    const activeAreaName = useSessionAreaName();
    const collections = useCollectionsSelector();
    const activeUser = useActiveUser();
    const areas = useAreasSelector();
    const settings = useUserSettings();
    const configType = useGetConfigPageType();
    const findAreasForCollection = useFindAreasForCollection();
    const [areasForSelectedCollection, setAreasForSelectedCollection] = useState(null);
    const [selectedAreaName, setSelectedAreaName] = useState(activeAreaName);
    const [selectedCollection, setSelectedCollection] = useState(activeCollectionName && (collections === null || collections === void 0 ? void 0 : collections.length)
        ? collections.find(({ collection }) => collection === activeCollectionName)
        : undefined);
    const [selectedArea, setSelectedArea] = useState(findArea({
        areaName: activeAreaName,
        areas: areasForSelectedCollection,
        collection: selectedCollection,
    }));
    useEffect(() => {
        if ((selectedCollection === null || selectedCollection === void 0 ? void 0 : selectedCollection.collection) !== activeCollectionName || (selectedArea === null || selectedArea === void 0 ? void 0 : selectedArea.name) !== activeAreaName) {
            const newSelectedCollection = activeCollectionName && (collections === null || collections === void 0 ? void 0 : collections.length)
                ? collections.find(({ collection }) => collection === activeCollectionName)
                : undefined;
            setSelectedCollection(newSelectedCollection);
            if ((newSelectedCollection === null || newSelectedCollection === void 0 ? void 0 : newSelectedCollection.collection) && activeAreaName) {
                const filteredAreas = findAreasForCollection(newSelectedCollection.collection, areas);
                const newSelectedArea = findArea({
                    areaName: activeAreaName,
                    areas: filteredAreas,
                    collection: newSelectedCollection,
                });
                if (newSelectedArea) {
                    setSelectedArea(newSelectedArea);
                    setSelectedAreaName(newSelectedArea.name);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        activeCollectionName,
        findAreasForCollection,
        collections,
        areas,
        activeAreaName,
    ]);
    const { defaultLoaded, defaultArea, defaultCollection } = useSetDefaultCollectionAndArea({
        setSelectedAreaName,
        setSelectedArea,
        setSelectedCollection,
    });
    // setting the valid areas based on the collection
    useEffect(() => {
        if (activeUser && (selectedCollection === null || selectedCollection === void 0 ? void 0 : selectedCollection.id) && (areas === null || areas === void 0 ? void 0 : areas.length)) {
            const filteredAreas = findAreasForCollection(selectedCollection === null || selectedCollection === void 0 ? void 0 : selectedCollection.collection, areas);
            setAreasForSelectedCollection(filteredAreas);
        }
    }, [activeUser, areas, selectedCollection, findAreasForCollection]);
    // setting selected area
    useEffect(() => {
        if (areasForSelectedCollection && !(areasForSelectedCollection === null || areasForSelectedCollection === void 0 ? void 0 : areasForSelectedCollection.length)) {
            setSelectedArea(undefined);
        }
        else if (areasForSelectedCollection && (selectedCollection === null || selectedCollection === void 0 ? void 0 : selectedCollection.id)) {
            if (hasArea(areasForSelectedCollection, selectedArea)) {
                // the selected area is valid for this collection, do nothing
                return;
            }
            const foundArea = findArea({
                areaName: selectedAreaName,
                areas: areasForSelectedCollection,
                collection: selectedCollection,
            });
            setSelectedArea(foundArea || areasForSelectedCollection[0]);
        }
    }, [selectedAreaName, selectedArea, areasForSelectedCollection, selectedCollection]);
    const handleSelectCollection = (collection) => {
        setSelectedCollection(collection);
    };
    const handleSelectArea = (area) => setSelectedArea(area);
    const isPageAreaDependent = configType === SearchForRetail.PageType.AREA;
    const isPageSiteDependent = configType === SearchForRetail.PageType.SITE;
    const collectionApplied = (selectedCollection === null || selectedCollection === void 0 ? void 0 : selectedCollection.collection) === activeCollectionName;
    const areaApplied = (selectedArea === null || selectedArea === void 0 ? void 0 : selectedArea.name) === activeAreaName;
    const collectionAndAreaApplied = collectionApplied && areaApplied;
    const isApplyButtonDisabled = !selectedCollection
        || ((isPageAreaDependent || isPageSiteDependent) && !selectedArea)
        || (collectionApplied && !isPageAreaDependent && !isPageSiteDependent)
        || collectionAndAreaApplied;
    const handleApplyClicked = useCallback(() => {
        if (!(selectedCollection === null || selectedCollection === void 0 ? void 0 : selectedCollection.collection)) {
            return;
        }
        setSessionCollectionName(selectedCollection.collection);
        if (isPageAreaDependent) {
            setSessionAreaName(selectedArea.name);
        }
        if (isPageSiteDependent) {
            setSessionAreaName(selectedArea.name);
            dispatch(switchSite(selectedArea.siteFilterId || null));
        }
        if (!isHomePage) {
            goToNewArea(selectedCollection.collection, selectedArea === null || selectedArea === void 0 ? void 0 : selectedArea.name);
        }
    }, [
        selectedCollection === null || selectedCollection === void 0 ? void 0 : selectedCollection.collection,
        selectedArea === null || selectedArea === void 0 ? void 0 : selectedArea.name,
        selectedArea === null || selectedArea === void 0 ? void 0 : selectedArea.siteFilterId,
        dispatch,
        isHomePage,
        goToNewArea,
        isPageAreaDependent,
        isPageSiteDependent,
    ]);
    // Auto-apply defaults when collection or area context is missing from session.
    // Triggers when neither has been set (fresh session) or when collection is present but
    // area was not persisted — e.g. after refreshing on a COLLECTION page whose URL has no
    // :area segment (Attribute Management, Filter Sets, Product Recommendations).
    const autoAppliedRef = useRef(false);
    useEffect(() => {
        const sessionMissingContext = !activeCollectionName || !activeAreaName;
        if (defaultLoaded
            && !autoAppliedRef.current
            && sessionMissingContext
            && defaultCollection
            && defaultArea
            && (selectedCollection === null || selectedCollection === void 0 ? void 0 : selectedCollection.collection)
            && (selectedArea === null || selectedArea === void 0 ? void 0 : selectedArea.name)) {
            autoAppliedRef.current = true;
            setSessionCollectionName(selectedCollection.collection);
            setSessionAreaName(selectedArea.name);
            // Only navigate when collection was also absent; if only area was missing
            // (refresh on a COLLECTION page) we just update Redux without redirecting.
            if (!isHomePage && !activeCollectionName) {
                goToNewArea(selectedCollection.collection, selectedArea.name);
            }
        }
    }, [
        defaultLoaded, activeCollectionName, activeAreaName,
        defaultCollection, defaultArea, selectedCollection, selectedArea,
        isHomePage, goToNewArea,
    ]);
    useEffect(() => {
        const filteredAreas = findAreasForCollection(activeCollectionName, areas);
        const selectedDefaultArea = filteredAreas.find(({ name }) => activeAreaName === name);
        dispatch(switchSite((selectedDefaultArea === null || selectedDefaultArea === void 0 ? void 0 : selectedDefaultArea.siteFilterId) || null));
    }, [dispatch, areas, activeCollectionName, activeAreaName, findAreasForCollection]);
    const handleSetAsDefaultClicked = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield updateUserSettings({
                preferredLanguage: settings.preferredLanguage,
                defaultCollection: selectedCollection === null || selectedCollection === void 0 ? void 0 : selectedCollection.collection,
                defaultArea: selectedArea === null || selectedArea === void 0 ? void 0 : selectedArea.name,
            }).unwrap();
            dispatch(addSuccess({ key: 'USER_SETTINGS_UPDATE_SUCCESS' }));
            if (!isApplyButtonDisabled) {
                handleApplyClicked();
            }
        }
        catch (e) {
            const errorMessage = 'USER_SETTINGS_UPDATE_ERROR';
            logError(errorMessage, e);
            dispatch(addError({ key: errorMessage }));
        }
    }), [selectedArea, selectedCollection, settings, dispatch, handleApplyClicked, isApplyButtonDisabled, updateUserSettings]);
    return (<ContextSelectors collections={collections} defaultCollection={defaultCollection} selectedCollection={selectedCollection} onSelectCollection={handleSelectCollection} areas={areasForSelectedCollection !== null && areasForSelectedCollection !== void 0 ? areasForSelectedCollection : []} defaultArea={defaultArea} selectedArea={selectedArea} onSelectArea={handleSelectArea} isApplyButtonDisabled={isApplyButtonDisabled} handleApplyClicked={handleApplyClicked} handleSetAsDefaultClicked={handleSetAsDefaultClicked}/>);
};
export default ContextSelectorsContainer;
